import { useState, useCallback, useEffect } from 'react';
import { ZoomClient } from '../types/index-types';

export function useAudio(zmClient: ZoomClient, mediaStream: any) {
  const [isAudioOn, setIsAudioOn] = useState(false);
  const [muteAll, setMuteAll] = useState(false);
  const excludedElementTypes = ["input", "textarea", "select"];

  const joinAudio = async () => {
    await mediaStream.startAudio()
  }

  const muteAudio = async () => {
    await mediaStream.muteAudio()
  }

  const unmuteAudio = async () => {
    await mediaStream.unmuteAudio()
  }

  const onCurrentAudioChange = useCallback((payload: any) => {
    const { action } = payload;
    if (action === 'muted') {
      setIsAudioOn(false)
    } else if (action === 'unmuted') {
      setIsAudioOn(true)
    } else if (action === 'join') {
      const currentUserInfo = zmClient.getCurrentUserInfo();
      const isMuted = currentUserInfo.muted;
      setIsAudioOn(!isMuted)
    }
  }, []);

  const onMicClick = async () => {
    try {
      const currentUserInfo = zmClient.getCurrentUserInfo();
      const isAudioEnabled = !!currentUserInfo.audio;
      const isMuted = currentUserInfo.muted;

      if (isAudioEnabled) {
        if (isMuted) {
          await unmuteAudio();
        } else {
          await muteAudio();
        }
      } else {
        await joinAudio();
        // if (zmClient.getCurrentUserInfo().muted) {
        //   await unmuteAudio();
        // }
      }
    } catch (err) {
      console.error('onMicClick error:', err);
    }
  };

  const handleGlobalMute = async () => {
    try {
      const currentUserInfo = zmClient.getCurrentUserInfo();
      const isAudioEnabled = !!currentUserInfo.audio;
      const isMuted = currentUserInfo.muted;

      const muteAllFlag = !muteAll
      setMuteAll(muteAllFlag)

      //when MuteAll is enabled
      if (muteAllFlag) {
        //if audio is enabled and user is not muted, mute audio
        if(isAudioEnabled){
          if (isMuted === false) {
            await muteAudio();
          }
        }
      } else {
        if (isAudioEnabled) {
          if (isMuted) {
            await unmuteAudio();
          }
        } else {
          await joinAudio();
          if (zmClient.getCurrentUserInfo().muted !== false) {
            await unmuteAudio();
          }
        }
      }
      const commandChannel = zmClient.getCommandClient();
      commandChannel.send(JSON.stringify({globalMute: muteAllFlag}))
    } catch (err) {
      console.error('handleGlobalMute error:', err);
    }
  }

  const handleHostAskUnmuteAudio = useCallback(async (muteFlag: boolean) => {
    try {
      const currentUserInfo = zmClient.getCurrentUserInfo();
      const isAudioEnabled = !!currentUserInfo.audio;
      const isMuted = currentUserInfo.muted;

      //when MuteAll is enabled
      if (muteFlag) {
        //if audio is enabled and user is not muted, mute audio
        if(isAudioEnabled){
          if (isMuted === false) {
            await muteAudio();
          }
        }
      } else {
        if (isAudioEnabled) {
          if (isMuted) {
            await unmuteAudio();
          }
        } else {
          await joinAudio();
          if (zmClient.getCurrentUserInfo().muted) {
            await unmuteAudio();
          }
        }
      }
    } catch (err) {
      console.error('handleHostAskUnmuteAudio error:', err);
    }
  }, [zmClient, unmuteAudio, muteAudio, joinAudio]);

  const onCommandChannelMessage = useCallback((payload: any) => {
    try {
      const { globalMute } = JSON.parse(payload.text);

      if(globalMute !== undefined){
        handleHostAskUnmuteAudio(globalMute).catch((err: any) => {
          console.error('onCommandChannelMessage handleHostAskUnmuteAudio error:', err);
        });
      }
    } catch (err) {
      console.error('onCommandChannelMessage error:', err);
    }
  }, [handleHostAskUnmuteAudio]);

  useEffect(() => {
    // Sync state on mount in case it changed while component was unmounted hamburger modal
    const currentUserInfo = zmClient?.getCurrentUserInfo();
    if (currentUserInfo) {
      setIsAudioOn(!!(currentUserInfo.audio && !currentUserInfo.muted));
    }
    
    zmClient.on('current-audio-change', onCurrentAudioChange);
    zmClient.on('command-channel-message', onCommandChannelMessage);
    return () => {
      zmClient.off('current-audio-change', onCurrentAudioChange);
      zmClient.off('command-channel-message', onCommandChannelMessage);
    };
  }, [zmClient, onCurrentAudioChange, onCommandChannelMessage]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (excludedElementTypes.includes(target.tagName.toLowerCase())) {
        return;
      }
      if (event.key === "~") {
        onMicClick();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onMicClick]);

  return {
    isAudioOn,
    joinAudio,
    muteAudio,
    unmuteAudio,
    onMicClick,
    muteAll,
    handleGlobalMute,
    handleHostAskUnmuteAudio
  };
}
